import React from "react";
import * as styles from "./UserGroupsSidePanel.module.scss";
import { Panel } from "cprime-foundational-components";
import { Stack } from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import { RootState, AppDispatch } from "../../../store/store";
import {
  createUserGroup,
  editUserGroup,
  parseConditions,
  resetGroupData,
} from "../../../store/slices/userGroupSlice";
import { deleteGroups } from "../../../store/slices/applicationListSlice";
import { triggerToast } from "../../../utils/toast";

import Button from "../../Common/CprimeButton";
interface UserGroupsSidePanelProps {
  open: boolean;
  appId?: number;
  title: string;
  onClose: () => void;
  BodyComponent: React.ReactNode;
  isEdit: boolean;
  groupId: number;
  setRefresh: () => void;
  adminUserGroups: boolean;
  getApplicationDetails: () => void;
  onRefreshData?: () => void;
}

const UserGroupsSidePanel: React.FC<UserGroupsSidePanelProps> = ({
  open,
  appId,
  title,
  onClose,
  BodyComponent,
  isEdit,
  groupId,
  setRefresh,
  adminUserGroups = false,
  getApplicationDetails,
  onRefreshData,
}) => {
  const scssClass = styles as any;
  const dispatch = useDispatch<AppDispatch>();
  const [loading, setLoading] = React.useState(false);
  const [deleteLoading, setDeleteLoading] = React.useState(false);
  const superAdminCreateGroupData = useSelector(
    (state: RootState) => state?.userGroups?.form,
  );

  const hasValidConditions = (obj: any) => {
    return (
      Object.keys(obj).length > 0 &&
      Object.values(obj).every(
        (value) => Array.isArray(value) && value.length > 0,
      )
    );
  };

  const isFormValid = () => {
    if (!superAdminCreateGroupData) return false;
    const {
      groupName,
      groupType,
      // distributionList,
      application,
      groupMembers,
      primaryContact,
      usageType,
      attributeType,
      attributeTypeValues,
    } = superAdminCreateGroupData;

    return (
      groupName?.trim() &&
      groupType?.trim() &&
      //distributionList?.trim() &&
      (adminUserGroups || application?.trim()) &&
      primaryContact?.trim() &&
      groupMembers?.length > 0 &&
      usageType?.length > 0 &&
      usageType[0].label &&
      (usageType[0].label !== "Profile Approver" ||
        (attributeType &&
          hasValidConditions(attributeType) &&
          attributeTypeValues &&
          hasValidConditions(attributeTypeValues) &&
          Object.keys(attributeType).length ===
            Object.keys(attributeTypeValues).length))
    );
  };

  const getMemberIdentity = (member: string) => {
    const parts = (member || "").split("|").map((item) => item.trim());
    return parts[1] || parts[0] || "";
  };

  const handleSave = async () => {
    setLoading(true);
    try {
      // --- Remove any new_group_members from groupMembers ---
      const cleanedGroupMembers = (
        superAdminCreateGroupData?.groupMembers || []
      ).filter((member: string) => {
        const memberName = getMemberIdentity(member);
        return !(superAdminCreateGroupData?.new_group_members || []).some(
          (newMem: string) => getMemberIdentity(newMem) === memberName,
        );
      });
      // Replace original data with cleaned version
      const finalData = {
        ...superAdminCreateGroupData,
        groupMembers: cleanedGroupMembers,
      };

      let result;
      if (isEdit) {
        const editPayload = {
          group_name: superAdminCreateGroupData?.groupName,
          group_type: superAdminCreateGroupData?.groupType,
          // specify_group_type: superAdminCreateGroupData?.specifyGroupType,
          dl: superAdminCreateGroupData?.distributionList,
          primary_contact: superAdminCreateGroupData?.primaryContact,
          group_members: finalData?.groupMembers,
          new_group_members: finalData?.new_group_members,
          associatedGroups: superAdminCreateGroupData?.associateGroups ?? [],
          associatedRoles: superAdminCreateGroupData?.associatedRoles ?? [],
          usage_type: superAdminCreateGroupData?.usageType[0]?.label ?? [],
          conditions: parseConditions(superAdminCreateGroupData),
        };
        result = await editUserGroup(
          groupId,
          editPayload,
          `api/groups/${groupId}`,
        );
      } else {
        result = await createUserGroup(
          superAdminCreateGroupData,
          adminUserGroups
            ? `api/groups/create?app_id=${appId}`
            : "api/groups/create",
        );
      }

      if (result) {
        const toastType = result?.error ? "error" : "success";
        triggerToast(result?.message, toastType);
        getApplicationDetails?.();
        onRefreshData?.();
      }
      setRefresh();
      dispatch(resetGroupData());
      onClose();
    } catch (err: any) {
      triggerToast(
        err?.response?.data?.message ??
          (isEdit
            ? `Failed to Update Group ${groupId}. Please try again.`
            : `Failed to Create Group. Please try again.`),
        "error",
      );
    } finally {
      setLoading(false);
    }
  };

  const deleteGroupCall = async () => {
    if (groupId) {
      setDeleteLoading(true);
      try {
        await dispatch(deleteGroups({ groupId }));
        triggerToast("Group deleted successfully", "success");
        getApplicationDetails?.();
        onRefreshData?.();
        onClose();
      } catch (error) {
        triggerToast(
          `Failed to delete group ${groupId}. please try again`,
          "error",
        );
      } finally {
        setRefresh();
        setDeleteLoading(false);
      }
    }
  };
  return (
    <>
      <Panel
        id="userGroupSidePanel"
        className={scssClass.userGroupMainPanel}
        sx={{
          "& .MuiDrawer-root": { padding: "0px" },
          "& .MuiPaper-root": {
            top: "104px",
            minWidth: "830px",
            height: "calc(100vh - 104px)",
            gap: "24px",
          },
        }}
        open={open}
        title={title}
        side="right"
        onClose={onClose}
        body={
          <>
            {BodyComponent}
            <Stack
              direction="row"
              justifyContent="space-between"
              p="1rem 1.5rem 0rem 0rem"
              boxSizing="border-box"
              className={scssClass.footerButtons}
            >
              {isEdit ? (
                <Button
                  variant="danger"
                  onClick={deleteGroupCall}
                  loading={deleteLoading}
                  disabled={loading}
                >
                  Delete Group
                </Button>
              ) : (
                <span></span>
              )}
              <Stack direction="row" gap={2}>
                <Button variant="tertiary" onClick={() => onClose()}>
                  Cancel
                </Button>
                <Button
                  variant="primary"
                  disabled={!isFormValid()}
                  onClick={handleSave}
                  loading={loading}
                >
                  Save Group
                </Button>
              </Stack>
            </Stack>
          </>
        }
      />
      <span
        style={{
          backgroundColor: "rgba(0, 0, 0, 0.5)",
          position: "fixed",
          width: "100vw",
          height: "calc(100vh - 104px)",
          zIndex: 1100,
          top: "104px",
          left: 0,
        }}
      ></span>
    </>
  );
};

export default UserGroupsSidePanel;
